(() => {
  const runtime = window.AuremeStaffstartDefaultOverrides?.runtime;
  if (!runtime) return;

  const {
    createEmptyMarkedElements,
    findAncestorUntilRoot,
    getTrimmedText,
    isBeforeElement,
    isCoordinateDetailPath,
    patchOrder,
    registerPatch,
    syncAttributeMarkers,
  } = runtime;

  const COORDINATE_ITEM_HEADING_TEXT = '着用アイテム';
  const COORDINATE_RELATED_HEADING_TEXT = 'このスタッフのコーディネート';
  const STAFF_DETAIL_LINK_SELECTOR = 'a[href*="/apps/staffstart/staffs/"]';
  const COORDINATE_DATE_PATTERN = /^\d{4}\.\d{2}\.\d{2}$/;
  const COORDINATE_STAFF_NAME_ATTRIBUTE = 'data-aureme-staffstart-coordinate-staff-name';
  const COORDINATE_PROFILE_ROW_ATTRIBUTE = 'data-aureme-staffstart-coordinate-profile-row';
  const COORDINATE_PROFILE_MEDIA_ATTRIBUTE = 'data-aureme-staffstart-coordinate-profile-media';
  const COORDINATE_PROFILE_IMAGE_FRAME_ATTRIBUTE =
    'data-aureme-staffstart-coordinate-profile-image-frame';
  const COORDINATE_PROFILE_IMAGE_ATTRIBUTE = 'data-aureme-staffstart-coordinate-profile-image';
  const COORDINATE_LAYOUT_ROW_ATTRIBUTE = 'data-aureme-staffstart-coordinate-layout-row';
  const COORDINATE_MEDIA_COLUMN_ATTRIBUTE = 'data-aureme-staffstart-coordinate-media-column';
  const COORDINATE_CONTENT_COLUMN_ATTRIBUTE = 'data-aureme-staffstart-coordinate-content-column';
  const COORDINATE_DETAIL_IMAGE_ATTRIBUTE = 'data-aureme-staffstart-coordinate-detail-image';
  const COORDINATE_ITEM_HEADING_ATTRIBUTE = 'data-aureme-staffstart-coordinate-item-heading';
  const COORDINATE_RELATED_HEADING_ATTRIBUTE = 'data-aureme-staffstart-coordinate-related-heading';
  const COORDINATE_DESCRIPTION_ATTRIBUTE = 'data-aureme-staffstart-coordinate-description';
  const COORDINATE_PRODUCT_NAME_ATTRIBUTE = 'data-aureme-staffstart-coordinate-product-name';
  const COORDINATE_TABLET_WIDTH_QUERY = '(min-width: 769px) and (max-width: 1023px)';
  const SWIPER_GEOMETRY_SELECTOR = `[${COORDINATE_MEDIA_COLUMN_ATTRIBUTE}] .mySwiper`;
  const SWIPER_WIDTH_TOLERANCE_PX = 1;

  /**
   * @typedef {{ destroyed?: boolean, update?: () => void, width?: number }} StaffstartSwiper
   */

  /**
   * @typedef {object} CoordinateDetailTypographyElements
   * @property {Set<HTMLElement>} profileRows - スタッフプロフィール行 marker 対象
   * @property {Set<HTMLElement>} profileMedia - スタッフ画像の外側ブロック marker 対象
   * @property {Set<HTMLElement>} profileImageFrames - スタッフ画像の丸型フレーム marker 対象
   * @property {Set<HTMLElement>} profileImages - スタッフ画像 marker 対象
   * @property {Set<HTMLElement>} staffNames - スタッフ名 marker 対象
   * @property {Set<HTMLElement>} layoutRows - 詳細メイン 2 カラム行 marker 対象
   * @property {Set<HTMLElement>} mediaColumns - 詳細メイン画像カラム marker 対象
   * @property {Set<HTMLElement>} contentColumns - 詳細テキストカラム marker 対象
   * @property {Set<HTMLElement>} detailImages - 詳細メイン画像 marker 対象
   * @property {Set<HTMLElement>} itemHeadings - 着用アイテム見出し marker 対象
   * @property {Set<HTMLElement>} relatedHeadings - このスタッフのコーディネート見出し marker 対象
   * @property {Set<HTMLElement>} descriptions - コーディネート説明 marker 対象
   * @property {Set<HTMLElement>} productNames - 着用アイテム商品名 marker 対象
   */

  /**
   * STAFF SNAP 詳細プロフィール用の空 marker 対象を都度返す。
   * @returns {Pick<CoordinateDetailTypographyElements, 'profileRows' | 'profileMedia' | 'profileImageFrames' | 'profileImages' | 'staffNames'>}
   */
  function createEmptyCoordinateProfileElements() {
    return {
      profileRows: createEmptyMarkedElements(),
      profileMedia: createEmptyMarkedElements(),
      profileImageFrames: createEmptyMarkedElements(),
      profileImages: createEmptyMarkedElements(),
      staffNames: createEmptyMarkedElements(),
    };
  }

  /**
   * STAFF SNAP 詳細用の空 marker 対象を都度返す。
   * @returns {CoordinateDetailTypographyElements}
   */
  function createEmptyCoordinateDetailTypographyElements() {
    return {
      ...createEmptyCoordinateProfileElements(),
      layoutRows: createEmptyMarkedElements(),
      mediaColumns: createEmptyMarkedElements(),
      contentColumns: createEmptyMarkedElements(),
      detailImages: createEmptyMarkedElements(),
      itemHeadings: createEmptyMarkedElements(),
      relatedHeadings: createEmptyMarkedElements(),
      descriptions: createEmptyMarkedElements(),
      productNames: createEmptyMarkedElements(),
    };
  }

  /**
   * STAFF SNAP 詳細のスタッフプロフィール行を返す。
   * @param {HTMLElement} root - STAFF START アプリの描画ルート
   * @returns {HTMLElement | null}
   */
  function findCoordinateProfileSummaryRow(root) {
    const profileLink = root.querySelector(STAFF_DETAIL_LINK_SELECTOR);
    if (!(profileLink instanceof HTMLAnchorElement)) return null;

    return findAncestorUntilRoot(profileLink.parentElement, root, (element) => {
      return Boolean(element.querySelector('h2'));
    });
  }

  /**
   * STAFF SNAP 詳細のスタッフプロフィール要素を一覧する。
   * @param {HTMLElement | null} summaryRow - スタッフプロフィール行
   * @returns {Pick<CoordinateDetailTypographyElements, 'profileRows' | 'profileMedia' | 'profileImageFrames' | 'profileImages' | 'staffNames'>}
   */
  function collectCoordinateProfileElements(summaryRow) {
    const profileElements = createEmptyCoordinateProfileElements();
    if (!summaryRow) return profileElements;

    const { profileRows, profileMedia, profileImageFrames, profileImages, staffNames } =
      profileElements;

    profileRows.add(summaryRow);

    const staffName = summaryRow.querySelector('h2');
    if (staffName instanceof HTMLElement) staffNames.add(staffName);

    const imageLink = summaryRow.querySelector(STAFF_DETAIL_LINK_SELECTOR);
    const image = imageLink?.querySelector('img');
    if (image instanceof HTMLElement) {
      profileImages.add(image);

      const frame = image.parentElement;
      if (frame instanceof HTMLElement) {
        profileImageFrames.add(frame);
      }

      const media = imageLink.parentElement;
      if (media instanceof HTMLElement) {
        profileMedia.add(media);
      }
    }

    return profileElements;
  }

  /**
   * STAFF SNAP 詳細の指定テキスト見出しを返す。
   * @param {HTMLElement} root - STAFF START アプリの描画ルート
   * @param {string} headingText - 見出しテキスト
   * @returns {HTMLElement | null}
   */
  function findCoordinateHeading(root, headingText) {
    for (const heading of root.querySelectorAll('h2')) {
      if (!(heading instanceof HTMLElement)) continue;
      if (getTrimmedText(heading) === headingText) return heading;
    }

    return null;
  }

  /**
   * STAFF SNAP 詳細の見出しを一覧する。
   * @param {HTMLElement | null} heading - 見出し
   * @returns {Set<HTMLElement>}
   */
  function collectCoordinateHeadings(heading) {
    return heading ? new Set([heading]) : new Set();
  }

  /**
   * STAFF SNAP 詳細のコーディネート説明候補かどうかを返す。
   * @param {HTMLParagraphElement} paragraph - 判定対象 paragraph
   * @returns {boolean}
   */
  function isCoordinateDescriptionParagraph(paragraph) {
    const text = getTrimmedText(paragraph);
    if (!text) return false;
    if (COORDINATE_DATE_PATTERN.test(text)) return false;
    if (paragraph.closest('a[href]')) return false;

    return true;
  }

  /**
   * STAFF SNAP 詳細のコーディネート説明を一覧する。
   * @param {HTMLElement} itemBlock - 着用アイテムブロック
   * @param {HTMLElement} contentBlock - STAFF SNAP 詳細の情報ブロック
   * @returns {Set<HTMLElement>}
   */
  function collectCoordinateDescriptions(itemBlock, contentBlock) {
    const descriptions = new Set();
    const paragraphsBeforeItems = Array.from(contentBlock.querySelectorAll('p')).filter(
      (paragraph) => {
        return paragraph instanceof HTMLParagraphElement && isBeforeElement(paragraph, itemBlock);
      },
    );
    // STAFF SNAP アプリの出力には識別可能な class/id が無いため、必ず先頭に出る日付表記を目印に説明文の開始位置を特定する
    const dateParagraph = paragraphsBeforeItems.find((paragraph) => {
      return COORDINATE_DATE_PATTERN.test(getTrimmedText(paragraph));
    });
    const descriptionCandidates = dateParagraph
      ? paragraphsBeforeItems.slice(paragraphsBeforeItems.indexOf(dateParagraph) + 1)
      : paragraphsBeforeItems;

    for (const paragraph of descriptionCandidates) {
      if (!isCoordinateDescriptionParagraph(paragraph)) continue;

      descriptions.add(paragraph);
      // STAFF START 詳細では、日付直後の paragraph がコーディネート説明として描画される。
      break;
    }

    return descriptions;
  }

  /**
   * STAFF SNAP 詳細の着用アイテム商品名を一覧する。
   * @param {HTMLElement} itemBlock - 着用アイテムブロック
   * @returns {Set<HTMLElement>}
   */
  function collectCoordinateProductNames(itemBlock) {
    const productNames = new Set();
    for (const link of itemBlock.querySelectorAll('a[href]')) {
      // STAFF START 詳細の着用アイテムリンクでは、先頭 paragraph が商品名として描画される。
      const productName = link.querySelector('p');
      if (!(productName instanceof HTMLElement)) continue;
      if (!getTrimmedText(productName)) continue;

      productNames.add(productName);
    }

    return productNames;
  }

  /**
   * STAFF SNAP 詳細のメイン 2 カラム要素を一覧する。
   * @param {HTMLElement | null} contentBlock - STAFF SNAP 詳細の情報ブロック
   * @returns {Pick<CoordinateDetailTypographyElements, 'layoutRows' | 'mediaColumns' | 'contentColumns' | 'detailImages'>}
   */
  function collectCoordinateLayoutElements(contentBlock) {
    const layoutElements = {
      layoutRows: createEmptyMarkedElements(),
      mediaColumns: createEmptyMarkedElements(),
      contentColumns: createEmptyMarkedElements(),
      detailImages: createEmptyMarkedElements(),
    };
    if (!contentBlock) return layoutElements;

    layoutElements.contentColumns.add(contentBlock);

    const mediaColumn = contentBlock.previousElementSibling;
    if (mediaColumn instanceof HTMLElement) {
      layoutElements.mediaColumns.add(mediaColumn);

      for (const image of mediaColumn.querySelectorAll('.swiper-slide img')) {
        if (image instanceof HTMLElement) layoutElements.detailImages.add(image);
      }
    }

    const layoutRow = contentBlock.parentElement;
    if (layoutRow instanceof HTMLElement) layoutElements.layoutRows.add(layoutRow);

    return layoutElements;
  }

  /**
   * STAFF SNAP 詳細の表示補正用 marker 対象をまとめて返す。
   * @param {HTMLElement} root - STAFF START アプリの描画ルート
   * @returns {CoordinateDetailTypographyElements}
   */
  function collectCoordinateDetailTypographyElements(root) {
    const profileElements = collectCoordinateProfileElements(findCoordinateProfileSummaryRow(root));
    const itemHeading = findCoordinateHeading(root, COORDINATE_ITEM_HEADING_TEXT);
    const relatedHeading = findCoordinateHeading(root, COORDINATE_RELATED_HEADING_TEXT);
    const itemBlock = itemHeading?.parentElement;
    const contentBlock = itemBlock?.parentElement;
    const layoutElements =
      contentBlock instanceof HTMLElement
        ? collectCoordinateLayoutElements(contentBlock)
        : collectCoordinateLayoutElements(null);
    const itemHeadings = collectCoordinateHeadings(itemHeading);
    const relatedHeadings = collectCoordinateHeadings(relatedHeading);
    const descriptions =
      itemBlock && contentBlock
        ? collectCoordinateDescriptions(itemBlock, contentBlock)
        : createEmptyMarkedElements();
    const productNames = itemBlock
      ? collectCoordinateProductNames(itemBlock)
      : createEmptyMarkedElements();

    return {
      ...profileElements,
      ...layoutElements,
      itemHeadings,
      relatedHeadings,
      descriptions,
      productNames,
    };
  }

  /**
   * STAFF SNAP 詳細の表示補正用 marker を反映する。
   * @param {HTMLElement} root - STAFF START アプリの描画ルート
   * @param {CoordinateDetailTypographyElements} elements - marker 対象
   */
  function syncCoordinateDetailTypographyMarkers(root, elements) {
    syncAttributeMarkers(root, COORDINATE_PROFILE_ROW_ATTRIBUTE, elements.profileRows);
    syncAttributeMarkers(root, COORDINATE_PROFILE_MEDIA_ATTRIBUTE, elements.profileMedia);
    syncAttributeMarkers(
      root,
      COORDINATE_PROFILE_IMAGE_FRAME_ATTRIBUTE,
      elements.profileImageFrames,
    );
    syncAttributeMarkers(root, COORDINATE_PROFILE_IMAGE_ATTRIBUTE, elements.profileImages);
    syncAttributeMarkers(root, COORDINATE_STAFF_NAME_ATTRIBUTE, elements.staffNames);
    syncAttributeMarkers(root, COORDINATE_LAYOUT_ROW_ATTRIBUTE, elements.layoutRows);
    syncAttributeMarkers(root, COORDINATE_MEDIA_COLUMN_ATTRIBUTE, elements.mediaColumns);
    syncAttributeMarkers(root, COORDINATE_CONTENT_COLUMN_ATTRIBUTE, elements.contentColumns);
    syncAttributeMarkers(root, COORDINATE_DETAIL_IMAGE_ATTRIBUTE, elements.detailImages);
    syncAttributeMarkers(root, COORDINATE_ITEM_HEADING_ATTRIBUTE, elements.itemHeadings);
    syncAttributeMarkers(root, COORDINATE_RELATED_HEADING_ATTRIBUTE, elements.relatedHeadings);
    syncAttributeMarkers(root, COORDINATE_DESCRIPTION_ATTRIBUTE, elements.descriptions);
    syncAttributeMarkers(root, COORDINATE_PRODUCT_NAME_ATTRIBUTE, elements.productNames);
  }

  /**
   * STAFF SNAP 詳細のタブレット幅補正が有効な画面幅かどうかを返す。
   * @returns {boolean}
   */
  function isCoordinateTabletWidth() {
    return window.matchMedia?.(COORDINATE_TABLET_WIDTH_QUERY).matches === true;
  }

  /**
   * Swiper v6 の updateSize と同じ計算で、padding を除いた実幅を返す。
   * @param {HTMLElement} swiperElement - Swiper ルート要素
   * @returns {number}
   */
  function getSwiperContentWidth(swiperElement) {
    const style = window.getComputedStyle(swiperElement);
    const paddingLeft = parseFloat(style.paddingLeft) || 0;
    const paddingRight = parseFloat(style.paddingRight) || 0;

    return swiperElement.clientWidth - paddingLeft - paddingRight;
  }

  /**
   * STAFF START app が生成した有効な Swiper インスタンスを返す。
   * @param {HTMLElement} swiperElement - Swiper ルート要素
   * @returns {StaffstartSwiper | null}
   */
  function getActiveSwiper(swiperElement) {
    const swiper = swiperElement.swiper;
    if (!swiper || swiper.destroyed) return null;

    return swiper;
  }

  /**
   * CSS 上書き後に Swiper の内部計測幅が実幅とずれているかどうかを返す。
   * @param {HTMLElement} swiperElement - Swiper ルート要素
   * @returns {boolean}
   */
  function needsSwiperGeometryUpdate(swiperElement) {
    const swiper = getActiveSwiper(swiperElement);
    if (!swiper) return false;

    const swiperWidth = Number(swiper.width);
    const contentWidth = getSwiperContentWidth(swiperElement);
    if (!Number.isFinite(swiperWidth) || !Number.isFinite(contentWidth) || contentWidth <= 0) {
      return false;
    }

    return Math.abs(swiperWidth - contentWidth) >= SWIPER_WIDTH_TOLERANCE_PX;
  }

  /**
   * Swiper の再計測が必要なメイン画像スライダーを一覧する。
   * @param {HTMLElement} root - STAFF START アプリの描画ルート
   * @returns {Set<HTMLElement>}
   */
  function collectCoordinateSwiperGeometryTargets(root) {
    const swiperElements = createEmptyMarkedElements();
    if (!isCoordinateTabletWidth()) return swiperElements;

    for (const swiperElement of root.querySelectorAll(SWIPER_GEOMETRY_SELECTOR)) {
      if (!(swiperElement instanceof HTMLElement)) continue;
      if (!needsSwiperGeometryUpdate(swiperElement)) continue;

      swiperElements.add(swiperElement);
    }

    return swiperElements;
  }

  /**
   * CSS のタブレット幅上書き後、app 側 Swiper の内部計測を現在の幅に揃える。
   * @param {HTMLElement} _root - STAFF START アプリの描画ルート
   * @param {Set<HTMLElement>} swiperElements - 再計測対象の Swiper ルート要素
   */
  function updateCoordinateSwiperGeometry(_root, swiperElements) {
    for (const swiperElement of swiperElements) {
      const swiper = getActiveSwiper(swiperElement);
      if (typeof swiper?.update !== 'function') continue;

      // app 側 Swiper は observer / resizeObserver が無効のため、CSS 幅変更後に明示的に再計測する。
      swiper.update();
    }
  }

  registerPatch({
    order: patchOrder.coordinateDetail.typography,
    isTargetRoute: isCoordinateDetailPath,
    collect: collectCoordinateDetailTypographyElements,
    createEmptyTargets: createEmptyCoordinateDetailTypographyElements,
    apply: syncCoordinateDetailTypographyMarkers,
  });

  registerPatch({
    order: patchOrder.coordinateDetail.swiperGeometry,
    isTargetRoute: isCoordinateDetailPath,
    collect: collectCoordinateSwiperGeometryTargets,
    createEmptyTargets: createEmptyMarkedElements,
    apply: updateCoordinateSwiperGeometry,
  });
})();
